import { useState } from 'react';
import { X } from '@phosphor-icons/react';
import { Dialog as DialogPrimitive } from 'radix-ui';

import walkthroughVideo from '../../../assets/walkthrough.mp4';
import walkthroughTypingVideo from '../../../assets/walkthrough-typing.mp4';
import walkthroughThreadVideo from '../../../assets/walkthrough-thread.mp4';
import walkthroughHighlight from '../../../assets/walkthrough-highlight.jpg';
import walkthroughNote from '../../../assets/walkthrough-note.jpg';
import walkthroughThread from '../../../assets/walkthrough-thread.jpg';

// First-time-user walkthrough for inline script comments (Figma
// 3405:75124 / 75260 / 75395). Three steps, each a looping demo clip
// inset on its gradient artwork — not a spotlight tour. Shown once per
// browser on the brand side (the mounting panel owns that gating +
// persistence); this component only walks the steps and reports done.

// Inner-slot sizes come from each step's Figma node (75124/75260/75395)
// — they differ per step. Step 3's clip is 1.77:1 vs its 2.02:1 slot
// and sits on a white ground, so it letterboxes (contain) into the
// frame's white center instead of cropping the typing indicator.
const STEPS = [
  {
    bg: walkthroughHighlight,
    video: walkthroughVideo,
    slotW: 260,
    slotH: 124,
    fit: 'cover',
    title: 'Highlight any line to comment on it',
    body: "Select a word or a full passage and a comment button appears right where you're reading.",
  },
  {
    bg: walkthroughNote,
    video: walkthroughTypingVideo,
    slotW: 260,
    slotH: 93,
    fit: 'cover',
    title: 'Write your note in context',
    body: 'The passage you picked stays pinned above while you type, so your feedback never loses its reference point.',
  },
  {
    bg: walkthroughThread,
    video: walkthroughThreadVideo,
    slotW: 269,
    slotH: 133,
    fit: 'contain',
    title: 'Turn it into a conversation',
    body: 'Every comment opens a thread, replies come in live and the whole discussion stays in one place.',
  },
] as const;

const pillButton =
  'flex h-10 items-center justify-center rounded-full px-4 text-[14px] font-medium leading-5';

export function CommentWalkthrough({
  onDone,
  onStepChange,
}: {
  onDone: () => void;
  /** Fired on step transitions so the mounting panel can re-anchor the
   *  card (step 1 hovers the script area; steps 2-3 hover the chat box). */
  onStepChange?: (step: number) => void;
}) {
  const [step, setStepState] = useState(0);
  const setStep = (next: number) => {
    setStepState(next);
    onStepChange?.(next);
  };
  const current = STEPS[step];
  if (!current) return null;
  const last = step === STEPS.length - 1;

  return (
    // Built on Radix's Dialog primitives for a real focus trap + Escape-to-
    // dismiss + aria-modal, same rationale as CommentIntroModal — a keyboard
    // user could otherwise tab straight through this card into the "inert"
    // script/chat behind it. No Portal/Overlay here (unlike the intro modal):
    // this card stays anchored inside ScriptReviewPanel's positioned wrapper,
    // which moves it per walkthrough step, so it can't be teleported to
    // document.body or dimmed by a full-screen backdrop without breaking that
    // positioning. Outside clicks are intentionally inert, matching the intro
    // modal's product decision.
    <DialogPrimitive.Root
      open
      modal
      onOpenChange={(open) => {
        if (!open) onDone();
      }}
    >
      <DialogPrimitive.Content
        aria-modal="true"
        onPointerDownOutside={(e) => e.preventDefault()}
        onInteractOutside={(e) => e.preventDefault()}
        className="bg-card relative flex w-[320px] flex-col gap-3.5 rounded-2xl border border-border p-4 drop-shadow-[0px_12px_15px_rgba(0,0,0,0.14)] outline-none"
      >
        {/* 288x152 media box (card 320 − 16px padding each side): gradient
          artwork as background fill, the step's clip centered at its own
          Figma slot size on top. React's `muted` prop races autoplay
          policy, so muted+play are forced imperatively on mount. */}
        <div className="relative flex h-[152px] w-full items-center justify-center overflow-hidden rounded-xl border border-border">
          <img src={current.bg} alt="" className="absolute inset-0 size-full object-cover" />
          <video
            key={current.video}
            ref={(el) => {
              if (el) {
                el.muted = true;
                // jsdom's play() returns undefined; browsers return a Promise.
                el.play()?.catch(() => {});
              }
            }}
            src={current.video}
            autoPlay
            muted
            loop
            playsInline
            className="relative rounded-[8px]"
            style={{ width: current.slotW, height: current.slotH, objectFit: current.fit }}
          />
        </div>

        <div className="flex flex-col gap-[5px]">
          <DialogPrimitive.Title asChild>
            <p className="text-[16px] leading-6 font-semibold text-foreground">{current.title}</p>
          </DialogPrimitive.Title>
          <DialogPrimitive.Description asChild>
            <p className="text-[14px] leading-5 text-muted-foreground">{current.body}</p>
          </DialogPrimitive.Description>
        </div>

        <div className="flex items-center">
          <p className="text-[12px] leading-4 font-medium text-muted-foreground">
            {step + 1} of {STEPS.length}
          </p>
          <div className="flex-1" />
          <div className="flex items-center gap-2">
            {step > 0 ? (
              <button
                type="button"
                onClick={() => setStep(step - 1)}
                className={`${pillButton} bg-[#f4f4f5] text-black`}
              >
                Back
              </button>
            ) : null}
            <button
              type="button"
              onClick={() => (last ? onDone() : setStep(step + 1))}
              className={`${pillButton} bg-primary text-primary-foreground`}
            >
              {step === 0 ? 'Start' : last ? 'Finish' : 'Continue'}
            </button>
          </div>
        </div>

        <button
          type="button"
          onClick={onDone}
          aria-label="Dismiss walkthrough"
          className="bg-card absolute top-[9px] right-[9px] flex size-7 items-center justify-center rounded-full border border-[#f5f5f5] shadow-[0px_2px_6px_rgba(0,0,0,0.12)]"
        >
          <X size={15} />
        </button>
      </DialogPrimitive.Content>
    </DialogPrimitive.Root>
  );
}
